<template>
    <section class="uses-tailwind px-5 min-h-[calc(100vh_-_120px)]">
        <div class="top-buttons">
            <ui5-button @click="$router.push('/debugmenu')">
                {{ trans('general.back') }}
            </ui5-button>
        </div>

        <loader
            v-if="loading || !rendered"
            :cover="true"
        />

        <template v-else>
            <div class="flex flex-wrap gap-4 py-4">
                <div
                    v-for="flow in Object.keys(flowDefinitions)"
                    :key="flow"
                    class="flex items-center gap-2"
                    :title="
                        flowMetadata[flow]
                            ? `ID: ${flowMetadata[flow].flowId}, Label: ${flowMetadata[flow].label}`
                            : `Static flow: ${flow}`
                    "
                >
                    <ui5-switch
                        :id="flow"
                        :checked="activeFlows.includes(flow)"
                        @change="toggleFlow(flow, $event.target.checked)"
                    />
                    <ui5-label
                        :for="flow"
                        class="capitalize"
                    >
                        {{ flow.replace(/_/g, ' ').toLowerCase() }}
                    </ui5-label>
                </div>
            </div>
        </template>

        <mermaid-flow
            v-show="!loading"
            v-model="diagramSource"
            @rendered="rendered = true"
            class="overflow-auto bg-white p-4 rounded-2xl shadow ring-1 ring-gray-200"
        />
    </section>
</template>

<script>
import MermaidFlow from '../components/MermaidFlow.vue';
import Loader from '../components/loader/Loader.vue';
import flowApi from '../api/flow';
import statusApi from '../api/status';
import { mapGetters, mapMutations } from 'vuex';
import {
    CORE_DIAGRAM,
    FLOW_DIAGRAMS,
} from '../shared/InteractiveProductFlowsData.js';
import localization from '../mixins/localization';

// Mermaid diagram styling constants
const MERMAID_STYLES = {
    LINK_DEFAULT: 'stroke-width:2px,fill:none,stroke:#333',
    START_NODE: 'fill:#d4edda,stroke:#28a745,stroke-width:2px,color:#212529',
    FINISH_NODE: 'fill:#f8d7da,stroke:#dc3545,stroke-width:2px,color:#212529',
};

// Core diagram template with status placeholders (###CODE### format)
// IN = start state, AF = end state
const CORE_DIAGRAM_TEMPLATE = `
graph TD
linkStyle default ${MERMAID_STYLES.LINK_DEFAULT}
${CORE_DIAGRAM}
classDef start ${MERMAID_STYLES.START_NODE};
classDef finish ${MERMAID_STYLES.FINISH_NODE};
class IN start;
class AF finish;
`;

export default {
    name: 'InteractiveProductFlows',
    mixins: [localization],
    components: {
        MermaidFlow,
        Loader,
    },
    data() {
        return {
            rendered: false,
            flows: [],
            statuses: [],
            loading: true,
        };
    },
    computed: {
        ...mapGetters('interactiveProductFlows', [
            'getActiveFlows',
            'isFlowActive',
        ]),

        activeFlows() {
            return this.getActiveFlows;
        },
        // Core diagram with resolved placeholders
        coreLines() {
            if (!this.statuses || this.statuses.length === 0) {
                return '';
            }

            return this.resolveStatusPlaceholders(CORE_DIAGRAM_TEMPLATE);
        },

        // Flow definitions with resolved placeholders
        flowDefinitions() {
            if (!this.statuses || this.statuses.length === 0) {
                return {};
            }

            const resolved = {};
            Object.keys(FLOW_DIAGRAMS).forEach((key) => {
                resolved[key] = this.resolveStatusPlaceholders(
                    FLOW_DIAGRAMS[key]
                );
            });

            return resolved;
        },

        // Flow metadata from API
        flowMetadata() {
            if (
                !this.flows ||
                this.flows.length === 0 ||
                !this.flowDefinitions
            ) {
                return {};
            }

            const metadata = {};
            Object.keys(this.flowDefinitions).forEach((flowKey) => {
                const apiFlow = this.flows.find((f) => f.label === flowKey);
                metadata[flowKey] = apiFlow || null;
            });

            return metadata;
        },

        diagramSource() {
            if (!this.coreLines || !this.flowDefinitions) {
                return '';
            }

            return (
                this.coreLines +
                this.activeFlows.map((f) => this.flowDefinitions[f]).join('\n')
            );
        },
    },
    async mounted() {
        try {
            const [flowsResponse, statusesResponse] = await Promise.all([
                flowApi.fetchAllFlows(),
                statusApi.fetchAllStatuses(),
            ]);

            this.flows = flowsResponse.data.data || [];
            this.statuses = statusesResponse.data.data || [];
        } catch (err) {
            console.error('Error fetching flows or statuses:', err);
        } finally {
            this.loading = false;
        }
    },
    methods: {
        ...mapMutations('interactiveProductFlows', ['setActiveFlows']),

        // Helper to resolve status placeholders
        resolveStatusPlaceholders(text) {
            return text.replace(/###(\w+)###/g, (_, code) => {
                const status = this.statuses.find((s) => s.code === code);
                return status ? status.external_name : code;
            });
        },

        toggleFlow(flow, checked) {
            this.$store.commit('interactiveProductFlows/toggleFlow', {
                flow,
                checked,
            });
        },
    },
};
</script>

<style scoped>
.capitalize {
    text-transform: capitalize;
}
</style>
